import { PropType, StyleValue, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import styles from './index.module.scss';
import { ElTour, ElTourStep } from 'element-plus';
import { v4 as uuidv4 } from 'uuid';
import SVG from '@dts/shared/src/assets/svg';
import { throttle } from 'lodash-es';

import StepMask from './StepMask';
import Typography from '@/components/TextFormat/FormatElements/Typography';
import { NButton } from 'naive-ui';
import LineTag from '@/components/LineTag';
import { genStaticUrl } from '@dts/shared/src/utils/httpUrlConfig';
import { PRIMARY_COLOR } from '@dts/shared/src/config/color';

export interface StepBlockType {
  content: JSX.Element;
  style?: StyleValue;
  btnCtx: string;
  btnClick?: () => void;
}

export const StepsID = {
  stepBar: `step-${uuidv4()}`,
  quickEntry: `step-${uuidv4()}`,
  feedbacks: `step-${uuidv4()}`,
  assistant: `step-${uuidv4()}`,
  exploreMore: `step-${uuidv4()}`,
};

export const props = {
  class: {
    type: String,
    default: '',
  },
  style: {
    type: Object as PropType<StyleValue>,
    default: () => ({}),
  },
};
const TourSteps = defineComponent({
  setup: (props) => {
    const tourOpen = ref(true);
    const currIdx = ref(0);
    const handleMouseScrollDown = throttle((event: any) => {
      if (currIdx.value < 5) {
        return;
      }
      tourOpen.value = false;
      document.removeEventListener('wheel', handleMouseScrollDown);
    }, 1000);
    onMounted(() => {
      document.addEventListener('wheel', handleMouseScrollDown);
    });
    onUnmounted(() => {
      console.log('组件被销毁');
      document.removeEventListener('wheel', handleMouseScrollDown);
    });

    const StepBox = ({ content, style, btnCtx, btnClick }: StepBlockType) => {
      return (
        <div class={styles.stepBox} style={style}>
          <>{content}</>

          <div class={styles.btnBox}>
            <NButton
              ghost
              color={PRIMARY_COLOR}
              size={'large'}
              class={styles.btn}
              onClick={() => btnClick && btnClick()}
            >
              <Typography.Fig weight={400} size={16}>
                {btnCtx}
              </Typography.Fig>
              <SVG.HomePageArrow />
            </NButton>
          </div>

          <div class={styles.decTop}>
            <SVG.WhitePageAngle />
          </div>
          <div class={styles.decBottom}>
            <SVG.TourStepsLeftBottomBlock />
          </div>
        </div>
      );
    };

    const LastStep = ({ content, style, btnCtx, btnClick }: StepBlockType) => {
      return (
        <div class={styles.lastStep} style={style}>
          <>{content}</>

          <div class={styles.btnBox}>
            <NButton
              ghost
              color={PRIMARY_COLOR}
              size={'large'}
              class={styles.coverBtn}
              onClick={() => btnClick && btnClick()}
            >
              <Typography.Fig weight={400} size={16}>
                {btnCtx}
              </Typography.Fig>
            </NButton>
          </div>

          <div class={styles.decTop}>
            <SVG.WhitePageAngle />
          </div>
          <div class={styles.decBottom}>
            <SVG.TourStepsLeftBottomBlock />
          </div>
        </div>
      );
    };

    const Envelop = ({ content, style, btnCtx, btnClick }: StepBlockType) => {
      return (
        <div class={styles.seal} style={style}>
          <div class={styles.act}>
            <div class={styles.envelop}>
              <>{content}</>
              <div class={styles.decTop}>
                <SVG.WhitePageAngle />
              </div>
            </div>
          </div>

          <div class={styles.cover}>
            <div class={styles.ctx}>
              <NButton
                size={'large'}
                class={styles.coverBtn}
                onClick={() => btnClick && btnClick()}
              >
                <Typography.Text weight={400} size={23}>
                  {btnCtx}
                </Typography.Text>
              </NButton>
            </div>

            <SVG.TourStepSealCover />
          </div>
        </div>
      );
    };

    const Exit = () => {
      return (
        <div
          class={styles.exit}
          role="button"
          tabindex={0}
          onKeydown={() => {}}
          onClick={() => {
            tourOpen.value = false;
            document.removeEventListener('wheel', handleMouseScrollDown);
          }}
        >
          <SVG.Exit /> <Typography.H3 weight={300}>Exit</Typography.H3>
        </div>
      );
    };

    return () => (
      <div class={styles.TourSteps}>
        {currIdx.value === 5 && <StepMask />}
        {tourOpen.value && <Exit />}
        <ElTour
          class={styles.elTours}
          v-model={tourOpen.value}
          v-model:current={currIdx.value}
          targetAreaClickable={false}
          v-slots={{
            indicators: () => null,
          }}
        >
          <ElTourStep show-close={false} show-arrow={false}>
            <Envelop
              style={{ width: '6.6rem' }}
              btnCtx="Start Exploring"
              btnClick={() => {
                currIdx.value += 1;
              }}
              content={
                <>
                  <div class={styles.envelopCtx}>
                    <Typography.H3 weight={300} size={31}>
                      Invitation:
                    </Typography.H3>
                    <Typography.H3> Start Data & AI Transformation Journey</Typography.H3>
                    <LineTag
                      style={{ margin: '.32rem 0' }}
                      leftStyle={{ height: '.02rem', width: '4.13rem' }}
                      rightStyle={{ height: '.02rem', width: '1rem' }}
                    />
                    <Typography.Text>
                      Explore DTS New Era and keep pace with China Data & AI Transformation!
                    </Typography.Text>

                    <div class={styles.btmDec}>
                      <img
                        src={genStaticUrl('eraImages/dts-logo.png')}
                        alt="bg"
                        draggable={false}
                      />
                    </div>
                  </div>
                </>
              }
            />
          </ElTourStep>

          <ElTourStep target={`#${StepsID.stepBar}`} show-close={false} show-arrow={false}>
            <StepBox
              style={{
                width: '5.62rem',
                marginLeft: '-1.48rem',
                // marginTop: '0.28rem'
              }}
              btnCtx="Got it"
              btnClick={() => {
                currIdx.value += 1;
              }}
              content={
                <>
                  <div class={styles.stepBar}>
                    <Typography.Text>Welcome to explore different channels.</Typography.Text>
                  </div>
                </>
              }
            />
          </ElTourStep>

          <ElTourStep
            placement="left"
            target={`#${StepsID.quickEntry}`}
            show-close={false}
            show-arrow={false}
          >
            <StepBox
              btnCtx="Got it"
              btnClick={() => {
                currIdx.value += 1;
              }}
              content={
                <>
                  <div class={styles.quickEntry}>
                    <Typography.Text>Quick entry to Data, AI and Use Cases.</Typography.Text>
                  </div>
                </>
              }
            />
          </ElTourStep>
          <ElTourStep
            placement="left-end"
            target={`#${StepsID.assistant}`}
            show-close={false}
            show-arrow={false}
          >
            <StepBox
              btnCtx="Got it"
              btnClick={() => {
                currIdx.value += 1;
              }}
              content={
                <>
                  <div class={styles.assistant}>
                    <Typography.Text>
                      DTO Assistant(DA) will always be there for you.
                    </Typography.Text>
                  </div>
                </>
              }
            />
          </ElTourStep>
          <ElTourStep target={`#${StepsID.feedbacks}`} show-close={false} show-arrow={false}>
            <StepBox
              btnCtx="Got it"
              btnClick={() => {
                currIdx.value += 1;
                console.log(currIdx.value);
              }}
              content={
                <>
                  <div class={styles.feedbacks}>
                    <Typography.Text>Send us your feedbacks.</Typography.Text>
                    <div class={styles.pic}>
                      <img
                        src={genStaticUrl('eraImages/change/gd-feedback.png')}
                        alt="feedback"
                        draggable={false}
                      />
                    </div>
                  </div>
                </>
              }
            />
          </ElTourStep>
          <ElTourStep
            placement="top"
            target={`#${StepsID.exploreMore}`}
            mask={false}
            show-close={false}
            show-arrow={false}
          >
            <StepBox
              btnCtx="Got it"
              btnClick={() => {
                tourOpen.value = false;
                document.removeEventListener('wheel', handleMouseScrollDown);
              }}
              content={
                <>
                  <div class={styles.exploreMore}>
                    <Typography.Text>Scroll down to explore more.</Typography.Text>
                  </div>
                </>
              }
            />
          </ElTourStep>

          {/* <ElTourStep placement="top" show-close={false} show-arrow={false}>
            <LastStep
              btnCtx="Start your journey."
              btnClick={() => {
                tourOpen.value = false;
              }}
              content={
                <div class={styles.congratulation}>
                  <Typography.Text>Congratulations on completing the boot</Typography.Text>
                  <Typography.Text>Now start your DTS life!</Typography.Text>
                </div>
              }
            />
          </ElTourStep> */}
        </ElTour>
      </div>
    );
  },
  props,
});
export default TourSteps;
